// EOSStore.jsx - EOS Store page
// Accessible to all players (window shopping allowed)
// Purchase requires Elite Operator Status
// Shows Gold A380 with roll odds and purchase button

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useWorldState } from "../components/state/WorldStateContext.jsx";
import { getEOSStatus, processA380Purchase, ROLL_PROBABILITIES, RARE_AIRCRAFT, EOS_REQUIREMENTS } from "../systems/EOSSystem.js";
import { calculateTier, TIER_THRESHOLDS } from "../systems/ReputationEngine.js";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { soundManager, SOUNDS } from "../systems/SoundManager";
import MythicReveal from "../components/MythicReveal.jsx";
// Icons removed - text-only UI

// Gold A380 price in dollars
const GOLD_A380_PRICE = 375_000_000;

// EOS Requirements Component - Shows locked state requirements
function EOSRequirements({ worldState, isSimulationMode, t }) {
  // Calculate current status for each requirement
  const requirements = useMemo(() => {
    const repScore = worldState?.reputation?.currentScore || 0;
    const tier = calculateTier(repScore);
    // OTP: Check if we have the stat, or calculate from flights if not
    let otp = worldState?.stats?.otp14Day;
    if (otp === undefined || otp === null) {
      // Calculate OTP on-demand if not yet set by daily tick
      const flights = worldState?.flights || [];
      const now = Date.now();
      const fourteenDaysMs = 14 * 24 * 60 * 60 * 1000;
      const last14Days = flights.filter(f => {
        if (f.phase !== 'completed') return false;
        const completed = new Date(f.completedAt || f.actualArrival || f.arrivalTime);
        return (now - completed.getTime()) <= fourteenDaysMs;
      });
      const onTime = last14Days.filter(f => (f.totalDelayMinutes || 0) <= 14).length;
      otp = last14Days.length > 0 ? onTime / last14Days.length : 1.0;
    }
    const cancellations = worldState?.stats?.cancellationsToday || 0;
    const safetyIncidents = worldState?.stats?.safetyIncidentsToday || 0;
    const dailyCashFlow = worldState?.stats?.dailyProfit || 0;
    const dailyDepartures = worldState?.stats?.dailyDepartures || 0;
    const fleetSize = worldState?.aircraft?.length || 0;
    const consecutiveDays = worldState?.eos?.consecutiveDays || 0;
    const crewWiseDays = worldState?.tech?.crewWiseDays || 0;
    const webLogicDays = worldState?.tech?.webLogicDays || 0;
    const hasCrewWise = worldState?.tech?.crewWise || false;
    const hasWebLogic = worldState?.tech?.webLogic || false;
    // EOS requires 10 consecutive days (per EOSSystem.js EOS_REQUIREMENTS.QUALIFICATION_DAYS)
    const requiredDays = EOS_REQUIREMENTS.QUALIFICATION_DAYS;

    const reqs = [
      {
        label: t('eosStatus.reqTier5'),
        current: `Tier ${tier} (${TIER_THRESHOLDS[tier]?.name || 'Unknown'})`,
        met: tier >= 5,
      },
      {
        label: t('eosStatus.reqOtp'),
        current: otp > 0 ? `${(otp * 100).toFixed(1)}%` : "---",
        met: otp >= 0.85,
      },
      {
        label: t('eosStatus.reqCancellations'),
        current: cancellations === 0 ? t('eosStatus.clean') : `${cancellations} ${t('eosStatus.today')}`,
        met: cancellations === 0,
      },
      {
        label: t('eosStatus.reqSafety'),
        current: safetyIncidents === 0 ? t('eosStatus.clean') : `${safetyIncidents} ${t('eosStatus.today')}`,
        met: safetyIncidents === 0,
      },
      {
        label: t('eosStatus.reqCashFlow'),
        current: dailyCashFlow >= 0 ? t('eosStatus.yes') : t('eosStatus.no'),
        met: dailyCashFlow >= 0,
      },
      {
        label: t('eosStatus.reqFlights'),
        current: `${dailyDepartures} ${t('eosStatus.today')}`,
        met: dailyDepartures >= 20,
      },
      {
        label: t('eosStatus.reqAircraft'),
        current: `${fleetSize} ${t('eosStatus.aircraft')}`,
        met: fleetSize >= 30,
      },
      {
        label: t('eosStatus.reqConsecutive', { days: requiredDays }),
        current: `${consecutiveDays}/${requiredDays} ${t('eosStatus.days')}`,
        met: consecutiveDays >= requiredDays,
      },
    ];

    // Add simulation-only requirements
    if (isSimulationMode) {
      reqs.splice(6, 0, {
        label: t('eosStatus.reqCrewWise'),
        current: hasCrewWise ? `${crewWiseDays} ${t('eosStatus.days')}` : t('eosStatus.notPurchased'),
        met: crewWiseDays >= 10,
        simulationOnly: true,
      });
      reqs.splice(7, 0, {
        label: t('eosStatus.reqWebLogic'),
        current: hasWebLogic ? `${webLogicDays} ${t('eosStatus.days')}` : t('eosStatus.notPurchased'),
        met: webLogicDays >= 10,
        simulationOnly: true,
      });
    }

    return reqs;
  }, [worldState, isSimulationMode]);

  const metCount = requirements.filter(r => r.met).length;

  return (
    <div
      className="rounded-lg p-4 mb-6"
      style={{ backgroundColor: "#0a0a0a", border: "1px solid #1a1a1a" }}
    >
      <h3 className="text-sm font-bold uppercase mb-4" style={{ color: "#737373" }}>
        {t('eosStatus.requirements')} ({metCount}/{requirements.length})
      </h3>
      <div className="space-y-3">
        {requirements.map((req, i) => (
          <div key={i} className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span
                className="w-5 h-5 flex items-center justify-center text-xs rounded"
                style={{
                  backgroundColor: req.met ? "#14532d" : "#1a1a1a",
                  color: req.met ? "#22c55e" : "#525252",
                  border: `1px solid ${req.met ? "#22c55e" : "#333"}`,
                }}
              >
                {req.met ? "Y" : "-"}
              </span>
              <span style={{ color: req.met ? "#a1a1a1" : "#525252" }}>
                {req.label}
              </span>
            </div>
            <span style={{ color: req.met ? "#22c55e" : "#737373" }}>
              {req.current}
            </span>
          </div>
        ))}
      </div>
      {/* Progress bar */}
      <div className="mt-4 h-2 rounded-full" style={{ backgroundColor: "#1a1a1a" }}>
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{
            width: `${(metCount / requirements.length) * 100}%`,
            backgroundColor: metCount === requirements.length ? "#22c55e" : "#f59e0b",
          }}
        />
      </div>
    </div>
  );
}

// Rarity Teaser Component - Shows silhouettes and odds
function RarityTeaser({ t }) {
  return (
    <div
      className="rounded-lg p-4 mb-6"
      style={{ backgroundColor: "#0a0a0a", border: "1px solid #1a1a1a" }}
    >
      <h3 className="text-sm font-bold uppercase mb-3" style={{ color: "#737373" }}>
        {t('eosStatus.collectibleTiers')}
      </h3>
      <div className="grid grid-cols-5 gap-2 mb-4">
        {[
          { key: "rare", color: "#22c55e", odds: "79.5%" },
          { key: "relic", color: "#f59e0b", odds: "10%" },
          { key: "anomalous", color: "#8b5cf6", odds: "5%" },
          { key: "legendary", color: "#ef4444", odds: "4.9%" },
          { key: "mythic", color: "#ec4899", odds: "0.1%" },
        ].map((tier) => (
          <div key={tier.key} className="text-center">
            <div
              className="w-10 h-10 mx-auto mb-1 rounded flex items-center justify-center"
              style={{ backgroundColor: "#1a1a1a", border: `1px solid ${tier.color}40` }}
            >
              <span style={{ color: tier.color, opacity: 0.5 }}>?</span>
            </div>
            <div className="text-xs" style={{ color: tier.color }}>{t(`eosStatus.${tier.key}`)}</div>
            <div className="text-xs" style={{ color: "#525252" }}>{tier.odds}</div>
          </div>
        ))}
      </div>
      <p className="text-xs text-center" style={{ color: "#525252" }}>
        {t('eosStatus.collectibleInfo')}
      </p>
    </div>
  );
}

// Rarity config for display
const RARITY_CONFIG = {
  rare: { label: "Rare", color: "#22c55e", symbol: "★", glow: "0 0 20px #22c55e" },
  relic: { label: "Relic", color: "#f59e0b", symbol: "♦", glow: "0 0 30px #f59e0b" },
  anomalous: { label: "Anomalous", color: "#8b5cf6", symbol: "⚡", glow: "0 0 40px #8b5cf6" },
  legendary: { label: "Legendary", color: "#ef4444", symbol: "♛", glow: "0 0 50px #ef4444" },
  mythic: { label: "Mythic", color: "#ec4899", symbol: "☠", glow: "0 0 60px #ec4899, 0 0 100px #ec4899" },
};

// Roll Animation Component
function RollAnimation({ isRolling, result, onComplete }) {
  const { t } = useTranslation();
  const [phase, setPhase] = useState("idle"); // idle, spinning, revealing, mythic, complete
  const [showCard, setShowCard] = useState(false);
  const [showMythicReveal, setShowMythicReveal] = useState(false);

  useEffect(() => {
    if (isRolling && result) {
      setPhase("spinning");
      setShowCard(false);
      setShowMythicReveal(false);

      // Spinning phase - then branch based on mythic or not
      const spinTimer = setTimeout(() => {
        if (result.rarity === "mythic") {
          // MYTHIC! Show epic reveal animation
          setPhase("mythic");
          setShowMythicReveal(true);
        } else {
          // Standard reveal
          setPhase("revealing");
          setShowCard(true);
          // Play appropriate drop sound based on rarity
          const dropSounds = {
            rare: SOUNDS.DROP_RARE,
            relic: SOUNDS.DROP_RELIC,
            anomalous: SOUNDS.DROP_ANOMALOUS,
            legendary: SOUNDS.DROP_LEGENDARY,
          };
          const dropSound = dropSounds[result.rarity] || SOUNDS.DROP_RARE;
          soundManager.play(dropSound);
        }
      }, 2000);

      // Complete phase (only for non-mythic, mythic completes on claim)
      const completeTimer = setTimeout(() => {
        if (result.rarity !== "mythic") {
          setPhase("complete");
        }
      }, 3500);

      return () => {
        clearTimeout(spinTimer);
        clearTimeout(completeTimer);
      };
    }
  }, [isRolling, result]);

  // Handle mythic claim
  const handleMythicClaim = useCallback(() => {
    setShowMythicReveal(false);
    setPhase("complete");
    // Small delay before completing to let mythic reveal fade
    setTimeout(() => {
      onComplete?.();
    }, 300);
  }, [onComplete]);

  if (!isRolling && phase === "idle") return null;

  // Show MythicReveal for mythic drops
  if (showMythicReveal && result?.rarity === "mythic") {
    return (
      <MythicReveal
        aircraft={result}
        onClaim={handleMythicClaim}
      />
    );
  }

  const rarityConfig = result ? RARITY_CONFIG[result.rarity] : RARITY_CONFIG.rare;

  return (
    <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50">
      <div className="text-center">
        {/* Spinning A380 */}
        {phase === "spinning" && (
          <div className="animate-spin" style={{ animationDuration: "0.5s" }}>
            <span style={{ color: "#f59e0b", fontSize: "6rem", fontWeight: "bold" }}>A380</span>
          </div>
        )}

        {/* Card Reveal */}
        {(phase === "revealing" || phase === "complete") && result && (
          <div
            className={`transition-all duration-500 ${showCard ? "scale-100 opacity-100" : "scale-50 opacity-0"}`}
          >
            {/* Card */}
            <div
              className="p-8 rounded-xl mx-auto max-w-md"
              style={{
                backgroundColor: "#0c0c0c",
                border: `3px solid ${rarityConfig.color}`,
                boxShadow: rarityConfig.glow,
              }}
            >
              {/* Rarity badge */}
              <Badge
                className="mb-4"
                style={{
                  backgroundColor: rarityConfig.color,
                  color: "#000",
                  fontSize: "1rem",
                  padding: "8px 16px",
                }}
              >
                <span className="mr-2">{rarityConfig.symbol}</span>
                {t(`eosStatus.${result.rarity}`)}
              </Badge>

              {/* Aircraft name */}
              <h2 className="text-3xl font-bold mb-2" style={{ color: rarityConfig.color }}>
                {result.name}
              </h2>

              {/* Flavor text */}
              {result.flavorText && (
                <p className="text-sm italic mb-4" style={{ color: "#a1a1a1" }}>
                  "{result.flavorText}"
                </p>
              )}

              {/* Aircraft image placeholder */}
              <div
                className="w-48 h-32 mx-auto mb-4 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: "#1a1a1a" }}
              >
                <span style={{ color: rarityConfig.color, fontSize: "2rem", fontWeight: "bold" }}>AIRCRAFT</span>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span style={{ color: "#525252" }}>{t('eosStatus.model')}</span>
                  <span className="ml-2" style={{ color: "#fff" }}>
                    {result.model || result.type}
                  </span>
                </div>
                {result.capacity && (
                  <div>
                    <span style={{ color: "#525252" }}>{t('eosStatus.capacity')}</span>
                    <span className="ml-2" style={{ color: "#fff" }}>
                      {result.capacity} {t('eosStatus.pax')}
                    </span>
                  </div>
                )}
                {result.range && (
                  <div>
                    <span style={{ color: "#525252" }}>{t('eosStatus.range')}</span>
                    <span className="ml-2" style={{ color: "#fff" }}>
                      {result.range} {t('eosStatus.nm')}
                    </span>
                  </div>
                )}
                {result.era && (
                  <div>
                    <span style={{ color: "#525252" }}>{t('eosStatus.era')}</span>
                    <span className="ml-2" style={{ color: "#fff" }}>
                      {result.era}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Add to Fleet button */}
            {phase === "complete" && (
              <Button
                className="mt-6"
                onClick={onComplete}
                style={{ backgroundColor: rarityConfig.color, color: "#000" }}
              >
                {t('eosStatus.addToFleet')}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Main EOS Store Component
export default function EOSStorePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { worldState, setWorldState } = useWorldState();

  const eosStatus = getEOSStatus(worldState);
  const hasEOS = worldState?.eos?.hasEOS || false;
  const cash = worldState?.airline?.cash || 0;
  const canAfford = cash >= GOLD_A380_PRICE * 100; // Cash is in cents
  const isSimulationMode = worldState?.gameMode === "simulation";

  // Roll state
  const [isRolling, setIsRolling] = useState(false);
  const [rollResult, setRollResult] = useState(null);

  // Roll history
  const totalRolls = worldState?.eos?.stats?.rareAircraftRolls || 0;
  const lastRoll = worldState?.eos?.stats?.lastRollRarity || null;

  // Handle purchase and roll
  const handlePurchase = useCallback(() => {
    if (!hasEOS || !canAfford || isRolling) return;

    // Process the purchase
    const result = processA380Purchase(worldState);

    if (result.success) {
      // Start roll animation
      soundManager.play(SOUNDS.ROLL_START);
      setRollResult(result.aircraft);
      setIsRolling(true);

      // Update world state with purchase result
      setWorldState((prev) => ({
        ...prev,
        airline: {
          ...prev.airline,
          cash: (prev.airline?.cash || 0) - GOLD_A380_PRICE * 100,
        },
        eos: {
          ...prev.eos,
          stats: {
            ...prev.eos?.stats,
            rareAircraftRolls: (prev.eos?.stats?.rareAircraftRolls || 0) + 1,
            lastRollRarity: result.aircraft.rarity,
          },
        },
      }));
    }
  }, [hasEOS, canAfford, isRolling, worldState, setWorldState]);

  // Handle roll complete - add aircraft to fleet
  const handleRollComplete = useCallback(() => {
    if (rollResult) {
      setWorldState((prev) => ({
        ...prev,
        aircraft: [
          ...(prev.aircraft || []),
          {
            id: `rare-${Date.now()}`,
            eosId: rollResult.aircraftId, // EOS ID for anomalous behavior matching (e.g., "the-bermudan")
            ...rollResult,
            registration: `N${Math.floor(Math.random() * 9000) + 1000}RC`,
            status: "available",
            isCollectible: true,
            acquiredAt: new Date().toISOString(),
          },
        ],
        eos: {
          ...prev.eos,
          stats: {
            ...prev.eos?.stats,
            rareAircraftOwned: (prev.eos?.stats?.rareAircraftOwned || 0) + 1,
          },
        },
        // Track collected aircraft for anomalous behavior checks (Zero Divided by Zero map haunting, etc.)
        collectedAircraft: [
          ...(prev.collectedAircraft || []),
          rollResult.id,
        ],
      }));
    }

    setIsRolling(false);
    setRollResult(null);
  }, [rollResult, setWorldState]);

  // Determine if purchase is allowed
  const canPurchase = hasEOS && canAfford && !isRolling;

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#000" }}>
      {/* Header */}
      <div
        className="sticky top-0 z-40 flex items-center justify-between p-4"
        style={{ backgroundColor: "#0a0a0a", borderBottom: "1px solid #1a1a1a" }}
      >
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/opscenter")}
            style={{ color: "#a1a1a1" }}
          >
            {t('eosStatus.back')}
          </Button>
          <div>
            <h1 className="text-xl font-bold" style={{ color: "#f59e0b" }}>
              {t('eosStatus.title')}
            </h1>
          </div>
        </div>

        {hasEOS ? (
          <Badge style={{ backgroundColor: "#052e16", color: "#22c55e", border: "1px solid #22c55e" }}>
            {t('eosStatus.eliteOperator')}
          </Badge>
        ) : (
          <Badge style={{ backgroundColor: "#1a1a1a", color: "#525252", border: "1px solid #333" }}>
            {t('eosStatus.viewingOnly')}
          </Badge>
        )}
      </div>

      {/* Main content */}
      <div className="max-w-2xl mx-auto p-6">
        {/* Gold A380 Card */}
        <div
          className="rounded-xl p-8 text-center relative"
          style={{
            backgroundColor: "#0c0c0c",
            border: hasEOS ? "2px solid #f59e0b" : "2px solid #333",
            boxShadow: hasEOS ? "0 0 40px rgba(245, 158, 11, 0.2)" : "none",
          }}
        >
          {/* Locked Overlay */}
          {!hasEOS && (
            <div
              className="absolute top-4 right-4 px-3 py-1 rounded text-sm font-bold"
              style={{ backgroundColor: "#1a1a1a", color: "#525252", border: "1px solid #333" }}
            >
              {t('eosStatus.locked')}
            </div>
          )}

          {/* A380 Image */}
          <div className="relative inline-block mb-6">
            <img
              src="./aircraft/a380.png"
              alt={t('eosStatus.goldA380')}
              style={{
                width: "160px",
                height: "auto",
                filter: hasEOS
                  ? "drop-shadow(0 0 20px #f59e0b) sepia(1) saturate(3) hue-rotate(-10deg) brightness(1.1)"
                  : "grayscale(1) brightness(0.3)",
                opacity: hasEOS ? 1 : 0.5,
              }}
            />
          </div>

          <h2 className="text-3xl font-bold mb-2" style={{ color: hasEOS ? "#f59e0b" : "#525252" }}>
            {t('eosStatus.goldA380')}
          </h2>
          <p className="mb-6" style={{ color: "#a1a1a1" }}>
            {t('eosStatus.purchaseToRoll')}
          </p>

          {/* Odds Table */}
          <div
            className="rounded-lg p-4 mb-6"
            style={{ backgroundColor: "#0a0a0a", border: "1px solid #1a1a1a" }}
          >
            <h3 className="text-sm font-bold uppercase mb-3" style={{ color: "#737373" }}>
              {t('eosStatus.rollOdds')}
            </h3>
            <div className="space-y-2">
              {Object.entries(ROLL_PROBABILITIES).map(([rarity, odds]) => {
                const config = RARITY_CONFIG[rarity.toLowerCase()];
                if (!config) return null;
                return (
                  <div key={rarity} className="flex items-center justify-between">
                    <span style={{ color: config.color }}>{config.label}</span>
                    <span style={{ color: "#a1a1a1" }}>{(odds * 100).toFixed(1)}%</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* EOS Status Message */}
          {hasEOS ? (
            <div
              className="mt-4 p-4 rounded-lg text-center"
              style={{
                backgroundColor: "#052e16",
                border: "1px solid #22c55e",
                color: "#22c55e"
              }}
            >
              <div className="font-bold mb-1">{t('eosStatus.statusActive')}</div>
              <div className="text-sm" style={{ color: "#86efac" }}>
                {t('eosStatus.rollsEarned')}
              </div>
            </div>
          ) : (
            <div
              className="mt-4 p-4 rounded-lg text-center"
              style={{
                backgroundColor: "#1a1a1a",
                border: "1px solid #333",
                color: "#737373"
              }}
            >
              <div className="font-bold mb-1">{t('eosStatus.statusRequired')}</div>
              <div className="text-sm">
                {t('eosStatus.achieveEos')}
              </div>
            </div>
          )}
        </div>

        {/* Requirements and Rarity Teaser - Only show when not EOS */}
        {!hasEOS && (
          <>
            <EOSRequirements worldState={worldState} isSimulationMode={isSimulationMode} t={t} />
            <RarityTeaser t={t} />
          </>
        )}

        {/* Roll History */}
        <div
          className="mt-6 p-4 rounded-lg"
          style={{ backgroundColor: "#0c0c0c", border: "1px solid #1a1a1a" }}
        >
          <div className="flex items-center justify-between">
            <span style={{ color: "#737373" }}>{t('eosStatus.yourRolls')}</span>
            <span style={{ color: "#fff" }}>{totalRolls} {t('eosStatus.total')}</span>
          </div>
          {lastRoll && (
            <div className="flex items-center justify-between mt-2">
              <span style={{ color: "#737373" }}>{t('eosStatus.lastRoll')}</span>
              <span style={{ color: RARITY_CONFIG[lastRoll]?.color || "#fff" }}>
                {t(`eosStatus.${lastRoll}`) || lastRoll}
              </span>
            </div>
          )}
        </div>

        {/* Collection count */}
        <div
          className="mt-4 p-4 rounded-lg"
          style={{ backgroundColor: "#0c0c0c", border: "1px solid #1a1a1a" }}
        >
          <div className="flex items-center justify-between">
            <span style={{ color: "#737373" }}>{t('eosStatus.collectiblesOwned')}</span>
            <span style={{ color: "#f59e0b" }}>
              {worldState?.eos?.stats?.rareAircraftOwned || 0}
            </span>
          </div>
        </div>
      </div>

      {/* Roll Animation Overlay */}
      <RollAnimation
        isRolling={isRolling}
        result={rollResult}
        onComplete={handleRollComplete}
      />
    </div>
  );
}
